import React, { useCallback, useEffect, useState } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useAudioContext } from './AudioContext';
import styles from './AudioFileList.module.scss';
import IconPlay from '../components/icons/IconPlay';
import IconClose from './icons/IconClose';
import useAudioFiles from '../hooks/useAudioFiles';
import usePlayCounts from '../hooks/usePlayCounts';
import AudioPlayer from './AudioPlayer';

const AudioFileList = () => {
  const audioContext = useAudioContext();
  const { audioFiles, loading } = useAudioFiles();
  const { playCounts, incrementPlayCount, encodeKey } = usePlayCounts();
  const [currentFileIndex, setCurrentFileIndex] = useState(null);

  useEffect(() => {
    const handlePlayAudio = async (fileRef, trackName) => {
      const storage = getStorage();
      try {
        const downloadURL = await getDownloadURL(ref(storage, fileRef.fullPath));
        audioContext.setSelectedAudio(downloadURL);
        audioContext.setCurrentTrackName(trackName);
        incrementPlayCount(trackName);
      } catch (error) {
        console.error('Error getting audio URL:', error);
      }
    };

    if (currentFileIndex !== null) {
      const file = audioFiles[currentFileIndex];
      handlePlayAudio(file.ref, file.name);
    }
  }, [currentFileIndex, audioFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNextAudio = useCallback(() => {
    if (audioFiles.length === 0) return;
    setCurrentFileIndex((prev) =>
      prev === null ? 0 : (prev + 1) % audioFiles.length
    );
  }, [audioFiles.length]);

  const { setSelectedAudio, setCurrentTrackName } = audioContext;
  const handleCloseAudio = useCallback(
    (autoPlayNext = false) => {
      if (autoPlayNext) {
        handleNextAudio();
      } else {
        setSelectedAudio(null);
        setCurrentTrackName(null);
        setCurrentFileIndex(null);
      }
    },
    [handleNextAudio, setSelectedAudio, setCurrentTrackName]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (currentFileIndex === null && audioFiles.length > 0) setCurrentFileIndex(0);
      }
      if (e.code === 'ArrowRight' || e.code === 'ArrowDown') {
        e.preventDefault();
        handleNextAudio();
      }
      if (e.code === 'ArrowLeft' || e.code === 'ArrowUp') {
        e.preventDefault();
        if (audioFiles.length === 0) return;
        setCurrentFileIndex((prev) =>
          prev === null ? 0 : (prev - 1 + audioFiles.length) % audioFiles.length
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentFileIndex, audioFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <section className={styles.container}>
        <ul className={styles.AudioFileList}>
          {Array.from({ length: 8 }).map((_, i) => (
            <li key={i} className={styles.SkeletonItem}>
              <span className={styles.SkeletonLine} style={{ width: `${50 + (i % 4) * 12}%` }} />
            </li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      {audioContext.selectedAudio && (
        <div className={styles.Player}>
          <AudioPlayer
            selectedAudio={audioContext.selectedAudio}
            handleCloseAudio={handleCloseAudio}
            handleNextAudio={handleNextAudio}
            songName={audioFiles[currentFileIndex]?.name}
          />
        </div>
      )}

      <ul className={styles.AudioFileList}>
        {audioFiles.map((file, index) => {
          const isPlaying = currentFileIndex === index;
          const count = playCounts[encodeKey(file.name)];
          return (
            <li
              key={index}
              className={isPlaying ? styles.ListItem_IsPlaying : styles.ListItem}
            >
              <button onClick={() => isPlaying ? handleCloseAudio(false) : setCurrentFileIndex(index)}>
                <span className={styles.SongName}>
                  <p>
                    {file.timeCreated.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  {file.isNew && <span className={styles.NewLabel}>New</span>}
                  {`${(audioFiles.length - index).toString().padStart(3, '0')} • ${file.name}`}
                </span>
                <span className={styles.TrackMeta}>
                  {count > 0 && (
                    <span className={styles.PlayCount}>{count} {count === 1 ? 'play' : 'plays'}</span>
                  )}
                  {isPlaying ? <IconClose color='#B45309' /> : <IconPlay color='#B45309' />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default React.memo(AudioFileList);
